import React, { useState, useRef, useEffect } from 'react';
import DocumentEditor from '../../components/DocumentEditor';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { documentService } from '../../services/documentService';
import { apiCall, ENDPOINTS } from '../../config/api';
import { FiMaximize2, FiMinimize2, FiX, FiEdit2, FiCheck } from 'react-icons/fi';

interface Patient {
        id: number;
        first_name: string;
        last_name: string;
        date_of_birth: string;
        medical_record_number: string;
}

interface Contact {
        id: number;
        full_name?: string;
        first_name: string;
        last_name: string;
        company: string;
        email: string;
        contact_type_name?: string;
        contact_type: string | number;
}

const NewDocument: React.FC = () => {
        const navigate = useNavigate();
        const location = useLocation();
        const { id: documentId } = useParams<{ id: string }>();
        const { token } = useAuth();
        const [isSaving, setIsSaving] = useState(false);
        const [isFullscreen, setIsFullscreen] = useState(false);
        const editorRef = useRef<any>(null);
        const fullscreenEditorRef = useRef<any>(null);
        const [documentContent, setDocumentContent] = useState<string | null>(null);
        
        // Document metadata
        const [patientId, setPatientId] = useState('');
        const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
        const [title, setTitle] = useState('Untitled Document');
        const [isEditingTitle, setIsEditingTitle] = useState(false);
        const [documentType, setDocumentType] = useState('clinical_note');
        const [description, setDescription] = useState('');
        const [providerId, setProviderId] = useState('');
        const [toContact, setToContact] = useState('');
        const [selectedToContact, setSelectedToContact] = useState<Contact | null>(null);
        const [recipientIsPatient, setRecipientIsPatient] = useState(false);
        const [selectedToPatient, setSelectedToPatient] = useState<Patient | null>(null);
        const [selectedTemplate, setSelectedTemplate] = useState('');
        
        // Search states
        const [patientSearch, setPatientSearch] = useState('');
        const [patientResults, setPatientResults] = useState<Patient[]>([]);
        const [showPatientResults, setShowPatientResults] = useState(false);
        const [contactSearch, setContactSearch] = useState('');
        const [contactResults, setContactResults] = useState<Contact[]>([]);
        const [showContactResults, setShowContactResults] = useState(false);
        const [toPatientSearch, setToPatientSearch] = useState('');
        const [toPatientResults, setToPatientResults] = useState<Patient[]>([]);
        const [showToPatientResults, setShowToPatientResults] = useState(false);
        const [providers, setProviders] = useState<any[]>([]);
        const [templates, setTemplates] = useState<any[]>([]);
        const [dataLoaded, setDataLoaded] = useState(false);
        const [hasRestoredState, setHasRestoredState] = useState(false);
        const [isEditingExistingDocument, setIsEditingExistingDocument] = useState(false);
        
        // Patient search with debounce
        useEffect(() => {
                if (patientSearch.length >= 2 && token && !selectedPatient) {
                        const timer = setTimeout(async () => {
                                try {
                                        const response = await apiCall(`/patients/?search=${encodeURIComponent(patientSearch)}`, {
                                                method: 'GET',
                                        }, token);
                                        
                                        if (response.ok) {
                                                const data = await response.json();
                                                const patients = data.results || data || [];
                                                
                                                // Additional client-side filtering to match typed text more precisely
                                                const searchLower = patientSearch.toLowerCase().trim();
                                                const filtered = patients.filter((patient: Patient) => {
                                                        const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
                                                        const reverseName = `${patient.last_name} ${patient.first_name}`.toLowerCase();
                                                        const mrn = (patient.medical_record_number || '').toLowerCase();
                                                        
                                                        return fullName.includes(searchLower) || 
                                                               reverseName.includes(searchLower) || 
                                                               mrn.includes(searchLower);
                                                });
                                                
                                                setPatientResults(filtered);
                                                setShowPatientResults(filtered.length > 0);
                                        }
                                } catch (error) {
                                        console.error('Error searching patients:', error);
                                }
                        }, 300);
                        
                        return () => clearTimeout(timer);
                } else if (selectedPatient) {
                        // If a patient is selected, hide dropdown and clear results
                        setShowPatientResults(false);
                        setPatientResults([]);
                } else {
                        setPatientResults([]);
                        setShowPatientResults(false);
                }
        }, [patientSearch, token, selectedPatient]);
        
        // Contact search with debounce
        useEffect(() => {
                if (contactSearch.length >= 2 && token && !selectedToContact) {
                        const timer = setTimeout(async () => {
                                try {
                                        const response = await apiCall(`${ENDPOINTS.CORE.CONTACTS}?search=${encodeURIComponent(contactSearch)}`, {
                                                method: 'GET',
                                        }, token);
                                        
                                        if (response.ok) {
                                                const data = await response.json();
                                                const contacts = data.results || data || [];
                                                console.log('[NewDocument] Contact search results:', contacts);
                                                setContactResults(contacts);
                                                setShowContactResults(contacts.length > 0);
                                        } else {
                                                console.error('[NewDocument] Contact search failed:', response.status, response.statusText);
                                        }
                                } catch (error) {
                                        console.error('[NewDocument] Error searching contacts:', error);
                                }
                        }, 300);
                        
                        return () => clearTimeout(timer);
                } else if (selectedToContact) {
                        setShowContactResults(false);
                } else {
                        setContactResults([]);
                        setShowContactResults(false);
                }
        }, [contactSearch, token, selectedToContact]);
        
        // To Patient search with debounce
        useEffect(() => {
                if (toPatientSearch.length >= 2 && token && !selectedToPatient && recipientIsPatient) {
                        const timer = setTimeout(async () => {
                                try {
                                        const response = await apiCall(`/patients/?search=${encodeURIComponent(toPatientSearch)}`, {
                                                method: 'GET',
                                        }, token);
                                        
                                        if (response.ok) {
                                                const data = await response.json();
                                                const patients = data.results || data || [];
                                                
                                                // Additional client-side filtering to match typed text more precisely
                                                const searchLower = toPatientSearch.toLowerCase().trim();
                                                const filtered = patients.filter((patient: Patient) => {
                                                        const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
                                                        const reverseName = `${patient.last_name} ${patient.first_name}`.toLowerCase();
                                                        const mrn = (patient.medical_record_number || '').toLowerCase();
                                                        
                                                        return fullName.includes(searchLower) || 
                                                               reverseName.includes(searchLower) || 
                                                               mrn.includes(searchLower);
                                                });
                                                
                                                setToPatientResults(filtered);
                                                setShowToPatientResults(filtered.length > 0);
                                        }
                                } catch (error) {
                                        console.error('[NewDocument] Error searching to patients:', error);
                                }
                        }, 300);
                        
                        return () => clearTimeout(timer);
                } else if (selectedToPatient) {
                        setShowToPatientResults(false);
                } else {
                        setToPatientResults([]);
                        setShowToPatientResults(false);
                }
        }, [toPatientSearch, token, selectedToPatient, recipientIsPatient]);
        
        // Load providers and templates on mount
        useEffect(() => {
                const loadData = async () => {
                        if (!token) return;
                        
                        try {
                                // Load providers from API
                                const providerResponse = await apiCall('/providers/providers/', {
                                        method: 'GET'
                                }, token);
                                
                                if (providerResponse.ok) {
                                        const providerData = await providerResponse.json();
                                        const providerList = providerData.results || providerData || [];
                                        // Filter to active providers only
                                        const activeProviders = providerList.filter((p: any) => p.is_active !== false);
                                        setProviders(activeProviders);
                                        console.log('[NewDocument] Loaded providers:', activeProviders.length);
                                }
                        } catch (error) {
                                console.error('Error loading providers:', error);
                        }
                        
                        // Load document types from database
                        try {
                                const response = await apiCall(ENDPOINTS.DOCUMENTS.TYPES, {
                                        method: 'GET',
                                }, token);
                                
                                if (response.ok) {
                                        const data = await response.json();
                                        const types = data.results || data || [];
                                        setTemplates(types);
                                        console.log('[NewDocument] Loaded templates:', types.length);
                                }
                        } catch (error) {
                                console.error('Error loading document types:', error);
                        }
                };
                
                loadData();
        }, [token]);
        
        // Mark data as loaded once providers and templates are populated
        useEffect(() => {
                if (providers.length > 0 && templates.length > 0 && !dataLoaded) {
                        console.log('[NewDocument] Data fully loaded, setting dataLoaded=true');
                        setDataLoaded(true);
                }
        }, [providers, templates, dataLoaded]);
        
        // Load patient from navigation state if provided
        useEffect(() => {
                const loadPatientFromState = async () => {
                        const state = location.state as { patientId?: number; patient?: Patient };
                        
                        if (state?.patientId && token) {
                                try {
                                        // Fetch full patient details
                                        const response = await apiCall(`/patients/${state.patientId}/`, {
                                                method: 'GET'
                                        }, token);
                                        
                                        if (response.ok) {
                                                const patient = await response.json();
                                                setSelectedPatient(patient);
                                                setPatientId(patient.id.toString());
                                                setPatientSearch(`${patient.first_name} ${patient.last_name}`);
                                                console.log('[NewDocument] Loaded patient from state:', patient);
                                        }
                                } catch (error) {
                                        console.error('Error loading patient from state:', error);
                                }
                        } else if (state?.patient) {
                                // Patient object was passed directly
                                const patient = state.patient;
                                setSelectedPatient(patient);
                                setPatientId(patient.id.toString());
                                setPatientSearch(`${patient.first_name} ${patient.last_name}`);
                                console.log('[NewDocument] Using patient from state:', patient);
                        }
                };
                
                loadPatientFromState();
        }, [location.state, token]);
        
        // Load document and handle fullscreen if provided
        useEffect(() => {
                const loadDocumentFromState = async () => {
                        // First, check if we have a document ID in the URL (editing mode)
                        if (documentId && token) {
                                console.log('[NewDocument] Loading document by ID:', documentId);
                                setIsEditingExistingDocument(true);
                                
                                try {
                                        // Fetch document metadata
                                        const docResponse = await apiCall(
                                                `/documents/${documentId}/`,
                                                { method: 'GET' },
                                                token
                                        );
                                        
                                        if (docResponse.ok) {
                                                const doc = await docResponse.json();
                                                console.log('[NewDocument] Full document data:', doc);
                                                
                                                setTitle(doc.title || 'Untitled Document');
                                                setDocumentType(doc.document_type || 'clinical_note');
                                                setDescription(doc.description || '');
                                                
                                                // Load patient if available
                                                if (doc.patient) {
                                                        setSelectedPatient(doc.patient);
                                                        setPatientId(doc.patient.id.toString());
                                                        setPatientSearch(`${doc.patient.first_name} ${doc.patient.last_name}`);
                                                }
                                                
                                                // Load provider if available (from document or upload_provider_id)
                                                if (doc.provider_id) {
                                                        setProviderId(doc.provider_id.toString());
                                                } else if (doc.upload_provider_id) {
                                                        setProviderId(doc.upload_provider_id.toString());
                                                }
                                                
                                                // Load recipient contact if available
                                                if (doc.recipient_contact) {
                                                        setSelectedToContact(doc.recipient_contact);
                                                        const displayName = doc.recipient_contact.full_name || 
                                                                          `${doc.recipient_contact.first_name} ${doc.recipient_contact.last_name}`.trim() || 
                                                                          doc.recipient_contact.company;
                                                        setContactSearch(displayName);
                                                } else if (doc.recipient_contact_id) {
                                                        // If we only have ID, try to fetch the contact
                                                        try {
                                                                const contactResponse = await apiCall(
                                                                        `/contacts/${doc.recipient_contact_id}/`,
                                                                        { method: 'GET' },
                                                                        token
                                                                );
                                                                if (contactResponse.ok) {
                                                                        const contact = await contactResponse.json();
                                                                        setSelectedToContact(contact);
                                                                        const displayName = contact.full_name || 
                                                                                          `${contact.first_name} ${contact.last_name}`.trim() || 
                                                                                          contact.company;
                                                                        setContactSearch(displayName);
                                                                }
                                                        } catch (err) {
                                                                console.error('Error loading recipient contact:', err);
                                                        }
                                                }
                                                
                                                // Load recipient patient if available (for patient-to-patient documents)
                                                if (doc.recipient_patient) {
                                                        setSelectedToPatient(doc.recipient_patient);
                                                        setRecipientIsPatient(true);
                                                        setToPatientSearch(`${doc.recipient_patient.first_name} ${doc.recipient_patient.last_name}`);
                                                } else if (doc.recipient_patient_id) {
                                                        // If we only have ID, try to fetch the patient
                                                        try {
                                                                const patientResponse = await apiCall(
                                                                        `/patients/${doc.recipient_patient_id}/`,
                                                                        { method: 'GET' },
                                                                        token
                                                                );
                                                                if (patientResponse.ok) {
                                                                        const patient = await patientResponse.json();
                                                                        setSelectedToPatient(patient);
                                                                        setRecipientIsPatient(true);
                                                                        setToPatientSearch(`${patient.first_name} ${patient.last_name}`);
                                                                }
                                                        } catch (err) {
                                                                console.error('Error loading recipient patient:', err);
                                                        }
                                                }
                                                
                                                console.log('[NewDocument] Document metadata loaded:', doc);
                                        }
                                        
                                        // Fetch document content
                                        const contentResponse = await apiCall(
                                                `/documents/${documentId}/content/`,
                                                { method: 'GET' },
                                                token
                                        );
                                        
                                        if (contentResponse.ok) {
                                                const contentData = await contentResponse.json();
                                                setDocumentContent(contentData.content || '');
                                                console.log('[NewDocument] Document content loaded');
                                        }
                                        
                                        // Auto-open in fullscreen when editing
                                        setTimeout(() => {
                                                setIsFullscreen(true);
                                        }, 100);
                                        
                                } catch (error) {
                                        console.error('Error loading document:', error);
                                }
                                return; // Exit early, don't process navigation state
                        }
                        
                        // Otherwise, check navigation state (for backwards compatibility)
                        const state = location.state as { 
                                document?: any; 
                                openFullscreen?: boolean;
                                patientId?: number;
                                patient?: Patient;
                        };
                        
                        if (state?.document && token) {
                                console.log('[NewDocument] Loading existing document from state:', state.document);
                                setIsEditingExistingDocument(true); // Mark as editing existing document
                                
                                try {
                                        // Fetch document content
                                        const response = await apiCall(
                                                `/documents/${state.document.id}/content/`,
                                                { method: 'GET' },
                                                token
                                        );
                                        
                                        if (response.ok) {
                                                const contentData = await response.json();
                                                setDocumentContent(contentData.content || '');
                                                setTitle(state.document.title || 'Untitled Document');
                                                console.log('[NewDocument] Document content loaded');
                                        }
                                } catch (error) {
                                        console.error('Error loading document content:', error);
                                }
                        }
                        
                        // Handle fullscreen mode
                        if (state?.openFullscreen) {
                                console.log('[NewDocument] Opening in fullscreen mode');
                                // Small delay to ensure content is loaded first
                                setTimeout(() => {
                                        setIsFullscreen(true);
                                }, 100);
                        }
                };
                
                loadDocumentFromState();
        }, [location.state, token, documentId]);
        
        // Save form state to localStorage
        useEffect(() => {
                const formState = {
                        title,
                        documentType,
                        description,
                        providerId,
                        selectedTemplate,
                        recipientIsPatient,
                        selectedPatient,
                        selectedToContact,
                        selectedToPatient,
                        patientSearch,
                        contactSearch,
                        toPatientSearch,
                        documentContent
                };
                console.log('[NewDocument] SAVING to localStorage:', formState);
                localStorage.setItem('newDocumentDraft', JSON.stringify(formState));
        }, [title, documentType, description, providerId, selectedTemplate, recipientIsPatient, 
            selectedPatient, selectedToContact, selectedToPatient, patientSearch, contactSearch, 
            toPatientSearch, documentContent]);
        
        // Restore form state from localStorage on mount - wait for data to load first
        useEffect(() => {
                if (!dataLoaded || hasRestoredState) return;
                
                // Don't restore if we have navigation state (new document from patient page)
                const state = location.state as { patientId?: number; patient?: Patient };
                if (state?.patientId || state?.patient) {
                        console.log('[NewDocument] Skipping restoration - navigation state present');
                        setHasRestoredState(true);
                        return;
                }
                
                const savedState = localStorage.getItem('newDocumentDraft');
                if (savedState) {
                        try {
                                const formState = JSON.parse(savedState);
                                console.log('[NewDocument] Restoring saved state:', formState);
                                
                                setTitle(formState.title || 'Untitled Document');
                                setDocumentType(formState.documentType || 'clinical_note');
                                setDescription(formState.description || '');
                                
                                // Type-safe restoration for IDs
                                const restoredProviderId = formState.providerId ? String(formState.providerId) : '';
                                const restoredTemplateId = formState.selectedTemplate ? String(formState.selectedTemplate) : '';
                                
                                console.log('[NewDocument] Restoring providerId:', restoredProviderId, 'Type:', typeof restoredProviderId);
                                console.log('[NewDocument] Restoring selectedTemplate:', restoredTemplateId, 'Type:', typeof restoredTemplateId);
                                console.log('[NewDocument] Providers available:', providers.length, 'IDs:', providers.map(p => ({ id: p.id, type: typeof p.id })));
                                
                                setProviderId(restoredProviderId);
                                setSelectedTemplate(restoredTemplateId);
                                setRecipientIsPatient(formState.recipientIsPatient || false);
                                
                                if (formState.selectedPatient) setSelectedPatient(formState.selectedPatient);
                                if (formState.selectedToContact) setSelectedToContact(formState.selectedToContact);
                                if (formState.selectedToPatient) setSelectedToPatient(formState.selectedToPatient);
                                if (formState.patientSearch) setPatientSearch(formState.patientSearch);
                                if (formState.contactSearch) setContactSearch(formState.contactSearch);
                                if (formState.toPatientSearch) setToPatientSearch(formState.toPatientSearch);
                                if (formState.documentContent) setDocumentContent(formState.documentContent);
                                
                                console.log('[NewDocument] State restored successfully');
                        } catch (error) {
                                console.error('Error restoring form state:', error);
                        }
                }
                
                setHasRestoredState(true);
        }, [dataLoaded, hasRestoredState, location.state]);
        
        const handlePatientSelect = (patient: Patient) => {
                setSelectedPatient(patient);
                setPatientId(patient.id.toString());
                setPatientSearch(`${patient.first_name} ${patient.last_name}`);
                setShowPatientResults(false);
        };
        
        const handleContactSelect = (contact: Contact) => {
                setSelectedToContact(contact);
                const displayName = contact.full_name || `${contact.first_name} ${contact.last_name}`.trim() || contact.company;
                setToContact(displayName);
                setContactSearch(displayName);
                setShowContactResults(false);
                setContactResults([]);
        };
        
        const clearContactSelection = () => {
                setSelectedToContact(null);
                setToContact('');
                setContactSearch('');
                setContactResults([]);
                setShowContactResults(false);
        };
        
        const handleToPatientSelect = (patient: Patient) => {
                setSelectedToPatient(patient);
                setToPatientSearch(`${patient.first_name} ${patient.last_name}`);
                setShowToPatientResults(false);
                setToPatientResults([]);
        };
        
        const clearToPatientSelection = () => {
                setSelectedToPatient(null);
                setToPatientSearch('');
                setToPatientResults([]);
                setShowToPatientResults(false);
        };
        
        const clearDraft = () => {
                if (window.confirm('Are you sure you want to clear all unsaved changes?')) {
                        localStorage.removeItem('newDocumentDraft');
                        // Reset all form fields
                        setTitle('');
                        setDocumentType('clinical_note');
                        setDescription('');
                        setProviderId('');
                        setSelectedTemplate('');
                        setRecipientIsPatient(false);
                        setSelectedPatient(null);
                        setSelectedToContact(null);
                        setSelectedToPatient(null);
                        setPatientSearch('');
                        setContactSearch('');
                        setToPatientSearch('');
                        setDocumentContent(null);
                }
        };
        
        const handleTitleEdit = () => {
                setIsEditingTitle(true);
        };
        
        const handleTitleSave = () => {
                setIsEditingTitle(false);
                if (!title) {
                        setTitle('Untitled Document');
                }
        };

        const handleFullscreenToggle = () => {
                if (!isFullscreen) {
                        // Entering fullscreen - save current content
                        if (editorRef.current?.documentEditor) {
                                try {
                                        const sfdt = editorRef.current.documentEditor.serialize();
                                        console.log('[NewDocument] Saving normal editor content, length:', sfdt.length);
                                        setDocumentContent(sfdt);
                                } catch (error) {
                                        console.error('[NewDocument] Error serializing normal editor:', error);
                                }
                        }
                        setIsFullscreen(true);
                } else {
                        // Exiting fullscreen - save fullscreen content
                        try {
                                if (fullscreenEditorRef.current?.documentEditor) {
                                        const sfdt = fullscreenEditorRef.current.documentEditor.serialize();
                                        console.log('[NewDocument] Saving fullscreen editor content, length:', sfdt.length);
                                        setDocumentContent(sfdt);
                                }
                        } catch (error) {
                                console.error('[NewDocument] Error serializing fullscreen editor:', error);
                                // Continue anyway - the content might still be in state
                        }
                        setIsFullscreen(false);
                }
        };

        const handleSave = async (blob: Blob) => {
                if (!token) {
                        alert('You must be logged in to save documents');
                        return;
                }

                if (!patientId) {
                        alert('Please enter a Patient ID');
                        return;
                }

                if (!title) {
                        alert('Please enter a document title');
                        return;
                }

                setIsSaving(true);
                try {
                        console.log('Saving document...', {
                                isEditing: isEditingExistingDocument,
                                documentId,
                                patientId,
                                title,
                                documentType,
                                providerId,
                                blobSize: blob.size
                        });
                        
                        let result;
                        if (isEditingExistingDocument && documentId) {
                                // Update existing document
                                console.log('Updating existing document:', documentId);
                                result = await documentService.updateDocumentBlob(
                                        documentId,
                                        blob,
                                        patientId,
                                        title,
                                        documentType,
                                        description,
                                        providerId,
                                        token
                                );
                                
                                const sizeInKB = (blob.size / 1024).toFixed(2);
                                alert(
                                        `Document updated successfully!\n\n` +
                                        `Title: ${result.title}\n` +
                                        `File: ${result.file_name}\n` +
                                        `Size: ${sizeInKB} KB`
                                );
                        } else {
                                // Create new document
                                console.log('Creating new document');
                                result = await documentService.uploadDocumentBlob(
                                        blob,
                                        patientId,
                                        title,
                                        documentType,
                                        description,
                                        providerId,
                                        token
                                );
                                
                                const sizeInKB = (blob.size / 1024).toFixed(2);
                                alert(
                                        `Document saved successfully to MinIO!\n\n` +
                                        `Title: ${result.title}\n` +
                                        `File: ${result.file_name}\n` +
                                        `Size: ${sizeInKB} KB\n` +
                                        `Path: patient_${patientId}/${result.file_name}`
                                );
                        }
                        
                        // Clear draft from localStorage
                        localStorage.removeItem('newDocumentDraft');
                        
                        // Navigate back to documents list
                        setTimeout(() => navigate('/documents'), 1500);
                } catch (err) {
                        console.error('Error saving document:', err);
                        alert(`Failed to save document: ${err instanceof Error ? err.message : 'Unknown error'}`);
                } finally {
                        setIsSaving(false);
                }
        };

        const handleSaveClick = async () => {
                if (!editorRef.current?.documentEditor) {
                        alert('Editor not initialized');
                        return;
                }
                
                if (!patientId) {
                        alert('Please select a patient');
                        return;
                }
                
                if (!title || title.trim() === '') {
                        alert('Please enter a document title');
                        return;
                }
                
                try {
                        // Get blob from Syncfusion DocumentEditor
                        const blob = await new Promise<Blob>((resolve, reject) => {
                                editorRef.current.documentEditor.saveAsBlob('Docx').then((blob: Blob) => {
                                        resolve(blob);
                                }).catch((error: any) => {
                                        reject(error);
                                });
                        });
                        
                        await handleSave(blob);
                } catch (err) {
                        console.error('Error getting editor content:', err);
                        alert('Failed to save document');
                }
        };

        return (
                <>
                        {/* Normal Page View */}
                        <div className={`m-2 p-2 ${isFullscreen ? 'hidden' : ''}`}>
                                {/* Document Metadata - Enhanced Header */}
                                <div className="bg-base-200 p-4 rounded-lg mb-4 shadow-lg overflow-visible pb-6">
                                        {/* Title Row */}
                                        <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center space-x-2 flex-1">
                                                        {isEditingTitle ? (
                                                                <div className="flex items-center space-x-2">
                                                                        <input
                                                                                type="text"
                                                                                value={title}
                                                                                onChange={(e) => setTitle(e.target.value)}
                                                                                onBlur={handleTitleSave}
                                                                                onKeyDown={(e) => e.key === 'Enter' && handleTitleSave()}
                                                                                className="input input-bordered text-lg font-semibold"
                                                                                placeholder="Document Title"
                                                                                autoFocus
                                                                        />
                                                                        <button
                                                                                onClick={handleTitleSave}
                                                                                className="btn btn-ghost btn-sm"
                                                                        >
                                                                                <FiCheck className="w-4 h-4 text-success" />
                                                                        </button>
                                                                </div>
                                                        ) : (
                                                                <div className="flex items-center space-x-2 cursor-pointer group" onDoubleClick={handleTitleEdit}>
                                                                        <h2 className="text-lg font-semibold group-hover:text-primary transition-colors">
                                                                                {title || 'Untitled Document'}
                                                                        </h2>
                                                                        <FiEdit2 className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                        <span className="text-xs opacity-60">(double-click to edit)</span>
                                                                </div>
                                                        )}
                                                </div>
                                                
                                                {/* Right Section - Action Buttons */}
                                                <div className="flex items-center space-x-2">
                                                        <button
                                                                onClick={handleSaveClick}
                                                                disabled={isSaving}
                                                                className="btn btn-primary btn-sm"
                                                                title="Save document"
                                                        >
                                                                {isSaving ? 'Saving...' : 'Save'}
                                                        </button>
                                                        <button
                                                                onClick={() => navigate('/documents')}
                                                                className="btn btn-ghost btn-sm"
                                                                title="Back to Documents"
                                                        >
                                                                ← Back to Documents
                                                        </button>
                                                </div>
                                        </div>
                                        
                                        {/* Metadata Row */}
                                        <div className="flex items-center space-x-4 text-sm flex-wrap gap-y-2 relative">
                                                {/* Patient Search */}
                                                <div className="relative">
                                                        <label className="text-xs opacity-70 mr-2">Patient:</label>
                                                        {selectedPatient ? (
                                                                <div className="badge badge-primary gap-2 h-12 px-4">
                                                                        <span className="text-sm">
                                                                                {selectedPatient.first_name} {selectedPatient.last_name}
                                                                        </span>
                                                                        <span className="text-xs opacity-70">
                                                                                (DOB: {selectedPatient.date_of_birth || 'N/A'})
                                                                        </span>
                                                                        <button
                                                                                onClick={() => {
                                                                                        setSelectedPatient(null);
                                                                                        setPatientSearch('');
                                                                                        setPatientId('');
                                                                                }}
                                                                                className="hover:text-error"
                                                                                title="Clear patient"
                                                                        >
                                                                                <FiX className="w-3 h-3" />
                                                                        </button>
                                                                </div>
                                                        ) : (
                                                                <input
                                                                        type="text"
                                                                        value={patientSearch}
                                                                        onChange={(e) => setPatientSearch(e.target.value)}
                                                                        onFocus={() => patientResults.length > 0 && setShowPatientResults(true)}
                                                                        className="input input-bordered w-64"
                                                                        placeholder="Search patient..."
                                                                />
                                                        )}
                                                        
                                                        {/* Patient Search Results Dropdown */}
                                                        {showPatientResults && patientResults.length > 0 && (
                                                                <div className="absolute top-full left-0 mt-1 w-80 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-[600px] overflow-y-auto z-[100]">
                                                                        {patientResults.map((patient) => (
                                                                                <div
                                                                                        key={patient.id}
                                                                                        onClick={() => handlePatientSelect(patient)}
                                                                                        className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
                                                                                >
                                                                                        <div className="font-medium text-base">
                                                                                                {patient.first_name} {patient.last_name}
                                                                                        </div>
                                                                                        <div className="text-sm opacity-60 space-y-1 mt-1">
                                                                                                <div>DOB: {patient.date_of_birth || 'N/A'}</div>
                                                                                                <div>MRN: {patient.medical_record_number || 'N/A'}</div>
                                                                                        </div>
                                                                                </div>
                                                                        ))}
                                                                </div>
                                                        )}
                                                </div>
                                                
                                                {/* Provider Selector (From) */}
                                                <div className="flex items-center">
                                                        <label className="text-xs opacity-70 mr-2">From:</label>
                                                        <select
                                                                value={providerId}
                                                                onChange={(e) => setProviderId(e.target.value)}
                                                                className="select select-bordered w-64"
                                                        >
                                                                <option value="">Select provider</option>
                                                                {providers.map((provider) => (
                                                                        <option key={provider.id} value={provider.id}>
                                                                                {provider.full_name || `${provider.first_name || ''} ${provider.last_name || ''}`}
                                                                        </option>
                                                                ))}
                                                        </select>
                                                </div>
                                                
                                                {/* Recipient Type Checkbox */}
                                                <div className="flex items-center">
                                                        <label className="text-xs opacity-70 mr-2">Patient:</label>
                                                        <input
                                                                type="checkbox"
                                                                checked={recipientIsPatient}
                                                                onChange={(e) => {
                                                                        setRecipientIsPatient(e.target.checked);
                                                                        // Clear selections when switching
                                                                        if (e.target.checked) {
                                                                                clearContactSelection();
                                                                        } else {
                                                                                clearToPatientSelection();
                                                                        }
                                                                }}
                                                                className="checkbox checkbox-primary"
                                                        />
                                                </div>
                                                
                                                {/* To Field with Contact/Patient Search */}
                                                <div className="flex items-center relative">
                                                        <label className="text-xs opacity-70 mr-2">To:</label>
                                                        {recipientIsPatient ? (
                                                                // Patient recipient
                                                                selectedToPatient ? (
                                                                        <div className="badge badge-info gap-2 h-12 px-4">
                                                                                <span className="text-sm">
                                                                                        {selectedToPatient.first_name} {selectedToPatient.last_name}
                                                                                </span>
                                                                                <button
                                                                                        onClick={clearToPatientSelection}
                                                                                        className="hover:text-error"
                                                                                        title="Clear patient"
                                                                                >
                                                                                        <FiX className="w-3 h-3" />
                                                                                </button>
                                                                        </div>
                                                                ) : (
                                                                        <div className="relative">
                                                                                <input
                                                                                        type="text"
                                                                                        value={toPatientSearch}
                                                                                        onChange={(e) => setToPatientSearch(e.target.value)}
                                                                                        className="input input-bordered w-64"
                                                                                        placeholder="Search patient..."
                                                                                />
                                                                                
                                                                                {/* To Patient Search Results Dropdown */}
                                                                                {showToPatientResults && toPatientResults.length > 0 && (
                                                                                        <div className="absolute top-full left-0 mt-1 w-80 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-[600px] overflow-y-auto z-[100]">
                                                                                                {toPatientResults.map((patient) => (
                                                                                                        <div
                                                                                                                key={patient.id}
                                                                                                                onClick={() => handleToPatientSelect(patient)}
                                                                                                                className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
                                                                                                        >
                                                                                                                <div className="font-medium text-base">
                                                                                                                        {patient.first_name} {patient.last_name}
                                                                                                                </div>
                                                                                                                <div className="text-sm opacity-60 space-y-1 mt-1">
                                                                                                                        <div>DOB: {patient.date_of_birth || 'N/A'}</div>
                                                                                                                        <div>MRN: {patient.medical_record_number || 'N/A'}</div>
                                                                                                                </div>
                                                                                                        </div>
                                                                                                ))}
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                )
                                                        ) : (
                                                                // Contact recipient
                                                                selectedToContact ? (
                                                                        <div className="badge badge-success gap-2 h-12 px-4">
                                                                                <span className="text-sm">
                                                                                        {selectedToContact.full_name || `${selectedToContact.first_name} ${selectedToContact.last_name}`.trim() || selectedToContact.company}
                                                                                </span>
                                                                                <button
                                                                                        onClick={clearContactSelection}
                                                                                        className="hover:text-error"
                                                                                        title="Clear contact"
                                                                                >
                                                                                        <FiX className="w-3 h-3" />
                                                                                </button>
                                                                        </div>
                                                                ) : (
                                                                        <div className="relative">
                                                                                <input
                                                                                        type="text"
                                                                                        value={contactSearch}
                                                                                        onChange={(e) => setContactSearch(e.target.value)}
                                                                                        className="input input-bordered w-64"
                                                                                        placeholder="Search contact..."
                                                                                />
                                                                                
                                                                                {/* Contact Search Results Dropdown */}
                                                                                {showContactResults && contactResults.length > 0 && (
                                                                                        <div className="absolute top-full left-0 mt-1 w-80 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-[600px] overflow-y-auto z-[100]">
                                                                                                {contactResults.map((contact) => (
                                                                                                        <div
                                                                                                                key={contact.id}
                                                                                                                onClick={() => handleContactSelect(contact)}
                                                                                                                className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
                                                                                                        >
                                                                                                                <div className="font-medium text-base">
                                                                                                                        {contact.full_name || `${contact.first_name} ${contact.last_name}`.trim() || contact.company}
                                                                                                                </div>
                                                                                                                <div className="text-sm opacity-60 space-y-1 mt-1">
                                                                                                                        <div>Type: {contact.contact_type_name || 'N/A'}</div>
                                                                                                                        {contact.company && <div>Company: {contact.company}</div>}
                                                                                                                        {contact.email && <div>Email: {contact.email}</div>}
                                                                                                                </div>
                                                                                                        </div>
                                                                                                ))}
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                )
                                                        )}
                                                </div>
                                                
                                                {/* Template Selector */}
                                                <div className="flex items-center">
                                                        <label className="text-xs opacity-70 mr-2">Template:</label>
                                                        <select
                                                                value={selectedTemplate}
                                                                onChange={(e) => setSelectedTemplate(e.target.value)}
                                                                className="select select-bordered w-64"
                                                                disabled={isEditingExistingDocument}
                                                                title={isEditingExistingDocument ? 'Template cannot be changed when editing an existing document' : ''}
                                                        >
                                                                <option value="">None</option>
                                                                {templates.map((template) => (
                                                                        <option key={template.id} value={template.id}>
                                                                                {template.name}
                                                                        </option>
                                                                ))}
                                                        </select>
                                                </div>
                                        </div>
                                </div>

                                {/* Editor with Fullscreen Button */}
                                <div className="bg-white rounded-lg shadow-lg relative">
                                        <button
                                                onClick={handleFullscreenToggle}
                                                className="absolute top-4 right-4 z-10 p-2 bg-white hover:bg-gray-100 rounded-lg shadow-md border border-gray-200 transition-colors"
                                                title="Open in fullscreen"
                                        >
                                                <FiMaximize2 className="w-5 h-5 text-gray-700" />
                                        </button>
                                        <DocumentEditor
                                                ref={editorRef}
                                                onSave={handleSave}
                                                height="calc(100vh - 320px)"
                                                initialContent={documentContent || undefined}
                                        />
                                </div>

                                {/* Save Status */}
                                {isSaving && (
                                        <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
                                                <div className="flex items-center">
                                                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                        </svg>
                                                        Saving document...
                                                </div>
                                        </div>
                                )}
                        </div>

                        {/* Fullscreen Modal - Shows SAME editor in fullscreen */}
                        {isFullscreen && (
                                <div className="fixed inset-0 z-50 bg-base-100 flex flex-col">
                                        {/* Fullscreen Header - Enhanced */}
                                        <div className="bg-base-200 px-6 py-3 shadow-lg overflow-visible">
                                                <div className="flex items-center justify-between mb-2">
                                                        {/* Left Section - Editable Title */}
                                                        <div className="flex items-center space-x-2 flex-1">
                                                                {isEditingTitle ? (
                                                                        <div className="flex items-center space-x-2">
                                                                                <input
                                                                                        type="text"
                                                                                        value={title}
                                                                                        onChange={(e) => setTitle(e.target.value)}
                                                                                        onBlur={handleTitleSave}
                                                                                        onKeyDown={(e) => e.key === 'Enter' && handleTitleSave()}
                                                                                        className="input input-bordered text-lg font-semibold"
                                                                                        placeholder="Document Title"
                                                                                        autoFocus
                                                                                />
                                                                                <button
                                                                                        onClick={handleTitleSave}
                                                                                        className="btn btn-ghost btn-sm"
                                                                                >
                                                                                        <FiCheck className="w-4 h-4 text-success" />
                                                                                </button>
                                                                        </div>
                                                                ) : (
                                                                        <div className="flex items-center space-x-2 cursor-pointer group" onDoubleClick={handleTitleEdit}>
                                                                                <h2 className="text-lg font-semibold group-hover:text-primary transition-colors">
                                                                                        {title || 'Untitled Document'}
                                                                                </h2>
                                                                                <FiEdit2 className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                        </div>
                                                                )}
                                                        </div>
                                                        
                                                        {/* Right Section - Action Buttons */}
                                                        <div className="flex items-center space-x-2">
                                                                <button
                                                                        onClick={handleSaveClick}
                                                                        disabled={isSaving}
                                                                        className="btn btn-primary btn-sm"
                                                                        title="Save document"
                                                                >
                                                                        {isSaving ? 'Saving...' : 'Save'}
                                                                </button>
                                                                <button
                                                                        onClick={handleFullscreenToggle}
                                                                        className="btn btn-ghost btn-sm btn-square"
                                                                        title="Exit fullscreen"
                                                                >
                                                                        <FiMinimize2 className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                        onClick={() => {
                                                                                if (window.confirm('Close editor? Any unsaved changes will be lost.')) {
                                                                                        setIsFullscreen(false);
                                                                                        navigate('/documents');
                                                                                }
                                                                        }}
                                                                        className="btn btn-ghost btn-sm btn-square"
                                                                        title="Close"
                                                                >
                                                                        <FiX className="w-5 h-5" />
                                                                </button>
                                                        </div>
                                                </div>
                                                
                                                {/* Metadata Row */}
                                                <div className="flex items-center space-x-4 text-sm relative">
                                                        {/* Patient Search */}
                                                        <div className="relative">
                                                                <label className="text-xs opacity-70 mr-2">Patient:</label>
                                                                {selectedPatient ? (
                                                                        <div className="badge badge-primary gap-2 h-12 px-4">
                                                                                <span className="text-sm">
                                                                                        {selectedPatient.first_name} {selectedPatient.last_name}
                                                                                </span>
                                                                                <span className="text-xs opacity-70">
                                                                                        (DOB: {selectedPatient.date_of_birth || 'N/A'})
                                                                                </span>
                                                                                <button
                                                                                        onClick={() => {
                                                                                                setSelectedPatient(null);
                                                                                                setPatientSearch('');
                                                                                                setPatientId('');
                                                                                        }}
                                                                                        className="hover:text-error"
                                                                                        title="Clear patient"
                                                                                >
                                                                                        <FiX className="w-3 h-3" />
                                                                                </button>
                                                                        </div>
                                                                ) : (
                                                                        <input
                                                                                type="text"
                                                                                value={patientSearch}
                                                                                onChange={(e) => setPatientSearch(e.target.value)}
                                                                                onFocus={() => patientResults.length > 0 && setShowPatientResults(true)}
                                                                                className="input input-bordered w-64"
                                                                                placeholder="Search patient..."
                                                                        />
                                                                )}
                                                                
                                                                {/* Patient Search Results Dropdown */}
                                                                {showPatientResults && patientResults.length > 0 && (
                                                                        <div className="absolute top-full left-0 mt-1 w-80 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-[600px] overflow-y-auto z-[100]">
                                                                                {patientResults.map((patient) => (
                                                                                        <div
                                                                                                key={patient.id}
                                                                                                onClick={() => handlePatientSelect(patient)}
                                                                                                className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
                                                                                        >
                                                                                                <div className="font-medium text-base">
                                                                                                        {patient.first_name} {patient.last_name}
                                                                                                </div>
                                                                                                <div className="text-sm opacity-60 space-y-1 mt-1">
                                                                                                        <div>DOB: {patient.date_of_birth || 'N/A'}</div>
                                                                                                        <div>MRN: {patient.medical_record_number || 'N/A'}</div>
                                                                                                </div>
                                                                                        </div>
                                                                                ))}
                                                                        </div>
                                                                )}
                                                        </div>
                                                        
                                                        {/* Provider Selector (From) */}
                                                        <div className="flex items-center">
                                                                <label className="text-xs opacity-70 mr-2">From:</label>
                                                                <select
                                                                        value={providerId}
                                                                        onChange={(e) => setProviderId(e.target.value)}
                                                                        className="select select-bordered w-64"
                                                                >
                                                                        <option value="">Select provider</option>
                                                                        {providers.map((provider) => (
                                                                                <option key={provider.id} value={provider.id}>
                                                                                        {provider.full_name || `${provider.first_name || ''} ${provider.last_name || ''}`}
                                                                                </option>
                                                                        ))}
                                                                </select>
                                                        </div>
                                                        
                                                        {/* Recipient Type Checkbox */}
                                                        <div className="flex items-center">
                                                                <label className="text-xs opacity-70 mr-2">Patient:</label>
                                                                <input
                                                                        type="checkbox"
                                                                        checked={recipientIsPatient}
                                                                        onChange={(e) => {
                                                                                setRecipientIsPatient(e.target.checked);
                                                                                // Clear selections when switching
                                                                                if (e.target.checked) {
                                                                                        clearContactSelection();
                                                                                } else {
                                                                                        clearToPatientSelection();
                                                                                }
                                                                        }}
                                                                        className="checkbox checkbox-primary"
                                                                />
                                                        </div>
                                                
                                                        {/* To Field with Contact/Patient Search */}
                                                        <div className="flex items-center relative">
                                                                <label className="text-xs opacity-70 mr-2">To:</label>
                                                                {recipientIsPatient ? (
                                                                        // Patient recipient
                                                                        selectedToPatient ? (
                                                                                <div className="badge badge-info gap-2 h-12 px-4">
                                                                                        <span className="text-sm">
                                                                                                {selectedToPatient.first_name} {selectedToPatient.last_name}
                                                                                        </span>
                                                                                        <button
                                                                                                onClick={clearToPatientSelection}
                                                                                                className="hover:text-error"
                                                                                                title="Clear patient"
                                                                                        >
                                                                                                <FiX className="w-3 h-3" />
                                                                                        </button>
                                                                                </div>
                                                                        ) : (
                                                                                <div className="relative">
                                                                                        <input
                                                                                                type="text"
                                                                                                value={toPatientSearch}
                                                                                                onChange={(e) => setToPatientSearch(e.target.value)}
                                                                                                className="input input-bordered w-64"
                                                                                                placeholder="Search patient..."
                                                                                        />
                                                                                        
                                                                                        {/* To Patient Search Results Dropdown */}
                                                                                        {showToPatientResults && toPatientResults.length > 0 && (
                                                                                                <div className="absolute top-full left-0 mt-1 w-80 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-[600px] overflow-y-auto z-[100]">
                                                                                                        {toPatientResults.map((patient) => (
                                                                                                                <div
                                                                                                                        key={patient.id}
                                                                                                                        onClick={() => handleToPatientSelect(patient)}
                                                                                                                        className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
                                                                                                                >
                                                                                                                        <div className="font-medium text-base">
                                                                                                                                {patient.first_name} {patient.last_name}
                                                                                                                        </div>
                                                                                                                        <div className="text-sm opacity-60 space-y-1 mt-1">
                                                                                                                                <div>DOB: {patient.date_of_birth || 'N/A'}</div>
                                                                                                                                <div>MRN: {patient.medical_record_number || 'N/A'}</div>
                                                                                                                        </div>
                                                                                                                </div>
                                                                                                        ))}
                                                                                                </div>
                                                                                        )}
                                                                                </div>
                                                                        )
                                                                ) : (
                                                                        // Contact recipient
                                                                        selectedToContact ? (
                                                                                <div className="badge badge-success gap-2 h-12 px-4">
                                                                                        <span className="text-sm">
                                                                                                {selectedToContact.full_name || `${selectedToContact.first_name} ${selectedToContact.last_name}`.trim() || selectedToContact.company}
                                                                                        </span>
                                                                                        <button
                                                                                                onClick={clearContactSelection}
                                                                                                className="hover:text-error"
                                                                                                title="Clear contact"
                                                                                        >
                                                                                                <FiX className="w-3 h-3" />
                                                                                        </button>
                                                                                </div>
                                                                        ) : (
                                                                                <div className="relative">
                                                                                        <input
                                                                                                type="text"
                                                                                                value={contactSearch}
                                                                                                onChange={(e) => setContactSearch(e.target.value)}
                                                                                                className="input input-bordered w-64"
                                                                                                placeholder="Search contact..."
                                                                                        />
                                                                                        
                                                                                        {/* Contact Search Results Dropdown */}
                                                                                        {showContactResults && contactResults.length > 0 && (
                                                                                                <div className="absolute top-full left-0 mt-1 w-80 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-[600px] overflow-y-auto z-[100]">
                                                                                                        {contactResults.map((contact) => (
                                                                                                                <div
                                                                                                                        key={contact.id}
                                                                                                                onClick={() => handleContactSelect(contact)}
                                                                                                                className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
                                                                                                        >
                                                                                                                <div className="font-medium text-base">
                                                                                                                        {contact.full_name || `${contact.first_name} ${contact.last_name}`.trim() || contact.company}
                                                                                                                </div>
                                                                                                                <div className="text-sm opacity-60 space-y-1 mt-1">
                                                                                                                        <div>Type: {contact.contact_type_name || 'N/A'}</div>
                                                                                                                        {contact.company && <div>Company: {contact.company}</div>}
                                                                                                                        {contact.email && <div>Email: {contact.email}</div>}
                                                                                                                </div>
                                                                                                        </div>
                                                                                                ))}
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                        )
                                                                )}
                                                        </div>
                                                        
                                                        {/* Template Selector */}
                                                        <div className="flex items-center">
                                                                <label className="text-xs opacity-70 mr-2">Template:</label>
                                                                <select
                                                                        value={selectedTemplate}
                                                                        onChange={(e) => setSelectedTemplate(e.target.value)}
                                                                        className="select select-bordered w-64"
                                                                        disabled={isEditingExistingDocument}
                                                                        title={isEditingExistingDocument ? 'Template cannot be changed when editing an existing document' : ''}
                                                                >
                                                                        <option value="">None</option>
                                                                        {templates.map((template) => (
                                                                                <option key={template.id} value={template.id}>
                                                                                        {template.name}
                                                                                </option>
                                                                        ))}
                                                                </select>
                                                        </div>
                                                </div>
                                        </div>

                                        {/* Fullscreen Editor - renders another instance */}
                                        <div className="flex-1 overflow-hidden">
                                                <DocumentEditor
                                                        ref={fullscreenEditorRef}
                                                        onSave={handleSave}
                                                        height="calc(100vh - 140px)"
                                                        initialContent={documentContent || undefined}
                                                />
                                        </div>
                                </div>
                        )}
                </>
        );
};

export default NewDocument;
